
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Topic } from '@/types/learning-path';
import type { PerformanceData } from '@/components/analytics/types';

interface AdaptiveRecommendation {
  topicId: string;
  confidence: number;
  recommendedDifficulty: 'easy' | 'medium' | 'hard';
  nextReviewDate?: string;
}

export function useAdaptiveLearning() {
  const [recommendations, setRecommendations] = useState<Record<string, AdaptiveRecommendation>>({});
  
  // Fetch user's performance data
  const { data: performanceData } = useQuery({
    queryKey: ['performance_history'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('performance_history')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching performance:', error);
        return [];
      }

      return data as PerformanceData[];
    }
  });
  
  // Calculate adaptive recommendations based on performance
  useEffect(() => {
    if (!performanceData?.length) return;
    
    // Group performance by subject
    const subjectPerformance: Record<string, PerformanceData[]> = {};
    performanceData.forEach(entry => {
      if (!entry.subject) return;
      
      if (!subjectPerformance[entry.subject]) {
        subjectPerformance[entry.subject] = [];
      }
      subjectPerformance[entry.subject].push(entry);
    });
    
    // Calculate recommendations for each subject
    const newRecommendations: Record<string, AdaptiveRecommendation> = {};
    
    Object.entries(subjectPerformance).forEach(([subject, entries]) => {
      // Get recent entries for the subject
      const recentEntries = entries.slice(0, 5);
      
      // Calculate average score
      const avgScore = recentEntries.reduce((acc, curr) => acc + curr.score, 0) / recentEntries.length;
      
      // Calculate mastery level
      const masteryConfidence = Math.min(avgScore / 100, 1);
      
      // Determine recommended difficulty
      let recommendedDifficulty: 'easy' | 'medium' | 'hard' = 'medium';
      if (avgScore < 50) recommendedDifficulty = 'easy';
      else if (avgScore > 85) recommendedDifficulty = 'hard';
      
      // Calculate next recommended review date based on spaced repetition algorithm
      const lastAttemptDate = new Date(recentEntries[0].created_at);
      let reviewIntervalDays = 1;
      
      // Simple spaced repetition - higher scores = longer intervals
      if (avgScore > 90) reviewIntervalDays = 14; // Two weeks
      else if (avgScore > 80) reviewIntervalDays = 7; // One week
      else if (avgScore > 70) reviewIntervalDays = 3; // Three days
      
      const nextReviewDate = new Date(lastAttemptDate);
      nextReviewDate.setDate(nextReviewDate.getDate() + reviewIntervalDays);
      
      // Find matching topic IDs based on subject
      const topicId = subject.toLowerCase().replace(/\s+/g, '-');
      
      newRecommendations[subject] = {
        topicId,
        confidence: masteryConfidence,
        recommendedDifficulty,
        nextReviewDate: nextReviewDate.toISOString()
      };
    });
    
    setRecommendations(newRecommendations);
  }, [performanceData]);
  
  // Get personalized topic recommendations
  const getRecommendedTopics = () => {
    if (!performanceData?.length) return [];
    
    return Object.entries(recommendations)
      .sort((a, b) => {
        const dateA = a[1].nextReviewDate ? new Date(a[1].nextReviewDate) : new Date();
        const dateB = b[1].nextReviewDate ? new Date(b[1].nextReviewDate) : new Date();
        return dateA.getTime() - dateB.getTime();
      })
      .map(([subject]) => subject);
  };
  
  // Get recommended difficulty for a topic
  const getRecommendedDifficulty = (topic: Topic) => {
    if (!topic.subject) return 'medium';
    
    const recommendation = Object.values(recommendations).find(
      rec => rec.topicId === topic.id || rec.topicId.includes(topic.subject?.toLowerCase() || '')
    );
    
    return recommendation?.recommendedDifficulty || 'medium';
  };
  
  // Calculate when a topic should be reviewed next
  const getNextReviewDate = (topic: Topic) => {
    if (!topic.subject) return null;
    
    const recommendation = Object.values(recommendations).find(
      rec => rec.topicId === topic.id || rec.topicId.includes(topic.subject?.toLowerCase() || '')
    );
    
    return recommendation?.nextReviewDate ? new Date(recommendation.nextReviewDate) : null;
  };
  
  return {
    recommendations,
    getRecommendedTopics,
    getRecommendedDifficulty,
    getNextReviewDate,
    performanceData
  };
}
